import Link from 'next/link';
import React, { useMemo } from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { HubTile } from '@web/features/hub-rework/HubTile';
import { useCurrentCompletedUser } from '@web/features/user/service';
import { Chip } from '@web/ui/components';
import { SharpImage } from '@web/ui/components/SharpImage';
import { useDesignsTokenTheme } from '@web/ui/theme/ThemeProvider';
import { trpcReact } from '@web/utils/trpc/trpcReact';

import { ProgramIcon } from '../brand-icons';

import personaCard1Dark from './instax-1-dark.png';
import personaCard1Light from './instax-1-light.png';
import personaCard2Dark from './instax-2-dark.png';
import personaCard2Light from './instax-2-light.png';
import personaCard3Dark from './instax-3-dark.png';
import personaCard3Light from './instax-3-light.png';
import personaCard4Dark from './instax-4-dark.png';
import personaCard4Light from './instax-4-light.png';
import { NewHoldersRow } from './NewHoldersRow';

const personaCardData = [
  { light: personaCard1Light, dark: personaCard1Dark },
  { light: personaCard2Light, dark: personaCard2Dark },
  { light: personaCard3Light, dark: personaCard3Dark },
  { light: personaCard4Light, dark: personaCard4Dark },
];

const ContentContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex: 1 0 0;
  align-self: stretch;
  ${({ theme }) => theme.breakpoints.down('lg')} {
    flex-direction: column-reverse;
    align-items: flex-start;
  }
`;

const LeftContainer = styled.div`
  display: flex;
  max-width: 25rem;
  padding-top: ${({ theme }) => theme.spacing('2000')};
  padding-right: ${({ theme }) => theme.spacing('2000')};
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing('2000')};
  flex: 1 0 0;
  align-self: stretch;
  ${({ theme }) => theme.breakpoints.down('lg')} {
    max-width: none;
    width: 100%;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('0750')};
`;

const Title = styled.h3`
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['4']};
  color: ${({ theme }) => theme.color.onBlue};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const Description = styled.p`
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  color: ${({ theme }) => theme.color.onBlue};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-content: center;
  gap: ${({ theme }) => theme.spacing('0500')};
  align-self: stretch;
`;

const ProgramStatsRow = styled.div`
  display: flex;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const ProgramStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  padding: ${({ theme }) => theme.spacing('1000')};
  gap: ${({ theme }) => theme.spacing('1500')};
  border-radius: ${({ theme }) => theme.shape.corner['1000']};
  background: ${({ theme }) => theme.color.surfaceContainer};
`;

const ProgramStatsLabel = styled.h3`
  font: ${({ theme }) => theme.typography.body.regular};
  font-weight: ${({ theme }) => theme.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  color: ${({ theme }) => theme.color.onSurface};
`;

const ProgramStatsValue = styled.p`
  font: ${({ theme }) => theme.typography.mono.regular};
  font-weight: ${({ theme }) => theme.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['5']};
  color: ${({ theme }) => theme.color.onSurface};
`;

const NewHolders = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  flex: 1 0 0;
  gap: ${({ theme }) => theme.spacing('2000')};
  border-radius: ${({ theme }) => theme.shape.corner['1000']};
  background: ${({ theme }) => theme.color.surfaceContainer};
`;

const NewHoldersHeader = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  justify-content: space-between;
  padding-top: ${({ theme }) => theme.spacing('0500')};
  padding-right: ${({ theme }) => theme.spacing('0500')};
  padding-left: ${({ theme }) => theme.spacing('1000')};
`;

const NewHoldersHeaderTitle = styled.div`
  font: ${({ theme }) => theme.typography.body.regular};
  font-weight: ${({ theme }) => theme.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  color: ${({ theme }) => theme.color.onSurface};
`;

const NewHoldersHeaderChip = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing('0500')};
  border-radius: ${({ theme }) => theme.shape.corner['0500']};
  background: ${({ theme }) => theme.color.redFixed};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  color: ${({ theme }) => theme.color.onRedFixed};
`;

const NewHoldersAvatars = styled.div`
  position: relative;
  display: flex;
  padding: 1rem;
  align-items: center;
  gap: -0.5rem;
  align-self: stretch;
`;

const PersonaStackContainer = styled.div`
  flex: 1 0 0;
  align-self: stretch;
  position: relative;
  display: flex;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    flex: none;
    height: 17.5rem;
  }
`;

const PersonaCard = styled(SharpImage)<{ index: number }>`
  position: absolute;
  max-width: 10rem;
  height: auto;
  top: ${({ index }) => index * 20}%;
  left: ${({ index }) => index * 20}%;
  z-index: ${({ index }) => index};

  ${({ theme }) => theme.breakpoints.down('xl')} {
    top: ${({ index }) => index * 15}%;
    left: ${({ index }) => index * 25}%;
  }
`;

const StyledChip = styled(Chip)`
  z-index: 2;
`;

export const PersonaProgramTile = () => {
  const t = useTranslations();
  const { isDarkMode } = useDesignsTokenTheme();

  const { data: holders } = trpcReact.user.holders.useQuery({ new: true });

  const { data: holdersCount } = trpcReact.user.holdersCount.useQuery();

  const completedUser = useCurrentCompletedUser();

  const followersData = useMemo(() => {
    if (holdersCount === undefined || !completedUser) {
      return undefined;
    }

    return {
      followers: (completedUser.user.followersCount / holdersCount) * 100,
      following: (completedUser.user.followingsCount / holdersCount) * 100,
    };
  }, [holdersCount]);

  const newHolders = holders && holders.items.length > 0;

  return (
    <Link href="/program">
      <HubTile
        textColor="onGreen"
        background="green"
        title={t('COMMUNITY_HUB_TILE_TITLE')}
        badge={<ProgramIcon size="3000" />}
        stateLayer="onGreen"
      >
        <ContentContainer>
          <LeftContainer>
            <TextContainer>
              <Title>{t('COMMUNITY_HUB_TILE_SUBTITLE')}</Title>
              <Description>{t('COMMUNITY_HUB_TILE_DESCRIPTION')}</Description>
            </TextContainer>

            <StatsContainer>
              <ProgramStatsRow>
                <ProgramStats>
                  <ProgramStatsLabel>
                    {t('FOLLOWERS_FOLLOWERS')}
                  </ProgramStatsLabel>
                  <ProgramStatsValue>{`${(followersData?.followers ?? 0).toFixed(2)}%`}</ProgramStatsValue>
                </ProgramStats>
                <ProgramStats>
                  <ProgramStatsLabel>
                    {t('FOLLOWERS_FOLLOWING')}
                  </ProgramStatsLabel>
                  <ProgramStatsValue>{`${(followersData?.following ?? 0).toFixed(2)}%`}</ProgramStatsValue>
                </ProgramStats>
              </ProgramStatsRow>

              <NewHolders>
                <NewHoldersHeader>
                  <NewHoldersHeaderTitle>
                    {t('NEW_HOLDERS_HUB_TILE_NEW_HOLDERS')}
                  </NewHoldersHeaderTitle>
                  <NewHoldersHeaderChip>{`${holdersCount ?? 0} New`}</NewHoldersHeaderChip>
                </NewHoldersHeader>

                <NewHoldersAvatars>
                  {newHolders ? (
                    <NewHoldersRow holders={holders.items} />
                  ) : (
                    <>
                      <StyledChip color="onPink" background="pink">
                        {t('NEW_HOLDERS_HUB_TILE_NO_NEW_HOLDERS')}
                      </StyledChip>
                    </>
                  )}
                </NewHoldersAvatars>
              </NewHolders>
            </StatsContainer>
          </LeftContainer>

          <PersonaStackContainer>
            {personaCardData.map((card, index) => (
              <PersonaCard
                key={index}
                index={index}
                src={isDarkMode ? card.dark : card.light}
                alt={`Persona ${index + 1}`}
              />
            ))}
          </PersonaStackContainer>
        </ContentContainer>
      </HubTile>
    </Link>
  );
};
